import React, {Component} from 'react';
import {Card, Banner, Spinner, TextContainer} from '@shopify/polaris';
import WebhookPropertySelector from './webhook-property-selector';
import If from './if';
import AsyncEvent from '@stak-digital/async-event';
import {getUserPreferences, getUserTemplates, updateAccountPreferences} from '../api';
import TemplateList from './template-list';
import {Layout, Block, CardContent, List} from './styled';

export default class App extends Component {
    promptDismissTimeout = null;

    state = {
        mergeTags: {},
        userPreferencesFetchEvent: new AsyncEvent(),
        updateMergeTagMappingEvent: new AsyncEvent()
    };

    componentDidMount() {
        this.fetchInitialData();
    }

    fetchInitialData = async () => {
        this.setState(state => {
            return {
                ...state,
                userPreferencesFetchEvent: state.userPreferencesFetchEvent.markAsExecuting()
            };
        });

        let userPreferences;

        try {
            userPreferences = await getUserPreferences(global.location.search);
        } catch (e) {
            this.setState(state => {
                return {
                    ...state,
                    userPreferencesFetchEvent: state.userPreferencesFetchEvent.reject(e),
                    hasFabtaleSessionCredentials: false
                };
            });
        }

        if (!userPreferences) {
            return;
        }

        const {fabtaleAPISessionToken, fabtaleAPIAccountName} = userPreferences;

        const hasFabtaleSessionCredentials = (
            !!fabtaleAPISessionToken &&
            !!fabtaleAPIAccountName
        );

        if (!hasFabtaleSessionCredentials) {
            this.setState(state => {
                return {
                    ...state,
                    userPreferencesFetchEvent: state.userPreferencesFetchEvent.reject(
                        new Error('Required credentials missing from payload')
                    ),
                    hasFabtaleSessionCredentials: false
                };
            });

            return;
        }

        let templates = [];

        try {
            const result = await getUserTemplates(
                fabtaleAPIAccountName,
                fabtaleAPISessionToken
            );

            templates = result.items;
        } catch(e) {
            this.setState(state => {
                return {
                    ...state,
                    userPreferencesFetchEvent: state.userPreferencesFetchEvent.reject(e),
                    hasFabtaleSessionCredentials: false
                };
            });

            return;
        }

        try {
            const selectedTemplate = templates.find(
                template => template.link === userPreferences.selectedTemplate
            );

            const { mergeTags } = userPreferences;

            this.setState(state => {
                return {
                    userTemplates: templates,
                    selectedTemplate: selectedTemplate || null,
                    mergeTags: mergeTags
                        ? JSON.parse(mergeTags)
                        : {},
                    hasFabtaleSessionCredentials,
                    userPreferencesFetchEvent: state.userPreferencesFetchEvent.resolve()
                };
            });
        } catch (e) {
            this.setState(state => {
                return {
                    ...state,
                    userPreferencesFetchEvent: state.userPreferencesFetchEvent.reject(e),
                    hasFabtaleSessionCredentials: false
                };
            });
        }
    };

    handleMergeTagMappingChanged = async (mergeTag, newValue) => {
        const mergeTags = {
            ...this.state.mergeTags,
            [mergeTag]: newValue
        };

        const oldMergeTags = this.state.mergeTags;

        this.setState(state => {
            return {
                ...state,
                // Presumptively set the merge tags to the new merge tags
                // so it doesn't lag in the UI
                mergeTags,
                updateMergeTagMappingEvent: state.updateMergeTagMappingEvent.markAsExecuting()
            };
        });

        try {
            await updateAccountPreferences({mergeTags});

            this.setState(state => {
                return {
                    ...state,
                    updateMergeTagMappingEvent: state.updateMergeTagMappingEvent.resolve()
                };
            });

            if (this.promptDismissTimeout) {
                clearTimeout(this.promptDismissTimeout);
            }

            this.promptDismissTimeout = setTimeout(() => {
                this.setState(state => {
                    return {
                        ...state,
                        updateMergeTagMappingEvent: state.updateMergeTagMappingEvent.reset()
                    };
                });
            }, 5000);
        } catch (e) {
            this.setState(state => {
                return {
                    ...state,
                    mergeTags: oldMergeTags,
                    updateMergeTagMappingEvent: state.updateMergeTagMappingEvent.reject()
                };
            });
        }
    };

    handleNewTemplateSelected = (newTemplateData) => {
        this.setState({
            selectedTemplate: newTemplateData
        });
    };

    renderMergeTagCardStatus = () => {
        const {updateMergeTagMappingEvent} = this.state;

        if (updateMergeTagMappingEvent.isExecuting) {
            return (
                <Banner icon="save" status="info">
                    Settings updating. Please wait.
                </Banner>
            );
        }

        if (updateMergeTagMappingEvent.isSuccessful) {
            return (
                <Banner icon="checkmark" status="success">
                    Settings successfully updated.
                </Banner>
            );
        }

        if (updateMergeTagMappingEvent.status === 'error') {
            return (
                <Banner icon="alert" status="critical">
                    Something went wrong. Please try again.
                </Banner>
            );
        }

        return null;
    };

    render() {
        const isNotConnectedToFabtaleApi = (
            (
                this.state.userPreferencesFetchEvent.isSuccessful &&
                !this.state.hasFabtaleSessionCredentials
            ) || (
                this.state.hasFabtaleSessionCredentials === false &&
                this.state.userPreferencesFetchEvent.error &&
                this.state.userPreferencesFetchEvent.error.message === 'Required credentials missing from payload'
            )
        );

        return (
            <Layout>
                <If condition={isNotConnectedToFabtaleApi}>
                    {() => (
                        <Block className="is-full-width">
                            <Banner status="critical" title="Account not yet set up">
                                This account has not been set up with the Fabtale API.
                                No Zings will be sent.
                            </Banner>
                        </Block>
                    )}
                </If>
                <If condition={!isNotConnectedToFabtaleApi && this.state.userPreferencesFetchEvent.error}>
                    {() => (
                        <Block className="is-full-width">
                            <Banner status="critical" title="Error">
                                {this.state.userPreferencesFetchEvent.error.message}
                            </Banner>
                        </Block>
                    )}
                </If>
                {!isNotConnectedToFabtaleApi && (
                    <Block>
                        <Card title={'Templates'}>
                            {this.state.userPreferencesFetchEvent.isSuccessful ? (
                                <TemplateList
                                    templates={this.state.userTemplates}
                                    selectedTemplate={this.state.selectedTemplate}
                                    onNewTemplateSelected={this.handleNewTemplateSelected}
                                />
                            ) : (
                                <CardContent>
                                    <Spinner/>
                                </CardContent>
                            )}
                        </Card>
                    </Block>
                )}
                {this.state.selectedTemplate && (
                    <Block>
                        <Card title={'Customer Data Insertion'}>
                            <Card.Section title={'Explanation'}>
                                <TextContainer>
                                    <p>
                                        The information that is generated in a
                                        Shopify purchase order can be inserted into
                                        the template at these designated spots.
                                    </p>
                                    <p>
                                        Choose which data from the purchase order
                                        should be inserted into the fabZing Receipt.
                                    </p>
                                </TextContainer>
                            </Card.Section>
                            <Card.Section>
                                <List>
                                    {Object.entries(JSON.parse(this.state.selectedTemplate.merge_tags)).map(([mergeTagType, mergeTag]) => {
                                        if (mergeTagType === 'text') {
                                            return mergeTag.map(tag => {
                                                return (
                                                    <li key={tag.tag}>
                                                        <WebhookPropertySelector
                                                            tagName={tag.tag}
                                                            onChange={this.handleMergeTagMappingChanged}
                                                            value={this.state.mergeTags[tag.tag] || ''}
                                                        />
                                                    </li>
                                                );
                                            });
                                        }

                                        return null;
                                    })}
                                </List>
                                <div>
                                    {this.renderMergeTagCardStatus()}
                                </div>
                            </Card.Section>
                        </Card>
                    </Block>
                )}
            </Layout>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/app.js